import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAdminUnlockScreen, useUpdateUnlockScreen } from "@/hooks/admin/unlockScreen";
import { AdminLayout } from "@/layouts/AdminLayout";

const schema = z.object({
  headline: z.string().trim().min(1, "Headline is required"),
  body: z.string(),
  unlocks: z.string(),
  button_label: z.string().trim().min(1, "Button label is required"),
});
type FormValues = z.infer<typeof schema>;

/**
 * Admin editor for the Dominion Free unlock screen (KC-498): the upsell Free
 * Participants see on their first dashboard visit and after their final Work
 * Package. One record; unlock items are edited one per line.
 */
export function AdminUnlockScreenPage() {
  const contentQuery = useAdminUnlockScreen();
  const update = useUpdateUnlockScreen();
  const [status, setStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { headline: "", body: "", unlocks: "", button_label: "" },
  });

  const content = contentQuery.data;
  useEffect(() => {
    if (content) {
      form.reset({
        headline: content.headline,
        body: content.body,
        unlocks: content.unlocks.join("\n"),
        button_label: content.button_label,
      });
    }
  }, [content, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    setStatus(null);
    try {
      await update.mutateAsync({
        headline: values.headline.trim(),
        body: values.body.trim(),
        unlocks: values.unlocks
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        button_label: values.button_label.trim(),
      });
      setStatus({ ok: true, message: "Saved. Free Participants see the change immediately." });
    } catch (err) {
      setStatus({ ok: false, message: err instanceof Error ? err.message : "Failed to save" });
    }
  });

  return (
    <AdminLayout>
      <div className="max-w-2xl space-y-4">
        <p className="text-sm text-gray-500">
          Shown to Free Participants on their first dashboard visit and after their final Work
          Package. The button takes them to checkout for the next paid Cohort.
        </p>
        {contentQuery.isPending ? (
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        ) : contentQuery.error ? (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            Failed to load the unlock screen.
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="headline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Headline</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Body</FormLabel>
                    <FormControl>
                      <Textarea rows={4} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unlocks"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What the paid programme unlocks (one per line)</FormLabel>
                    <FormControl>
                      <Textarea rows={6} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="button_label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Button label</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {status && (
                <p className={`text-sm ${status.ok ? "text-green-700" : "text-red-600"}`}>
                  {status.message}
                </p>
              )}
              <button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                Save
              </button>
            </form>
          </Form>
        )}
      </div>
    </AdminLayout>
  );
}
